import { useEffect, useState } from "react";

import clsx from "clsx";

import { useDebounce } from "@uidotdev/usehooks";
import {
  KeyIcon,
  PencilIcon,
  PlusIcon,
  UserMinusIcon,
  UserPlusIcon,
} from "@heroicons/react/24/outline";

import { Alert } from "../base/Alert";
import Card from "../shared/Card/Card";
import { Button } from "../base/Button";
import MiniTable from "../base/MiniTable";
import { TextField } from "../base/TextField";
import { IUser } from "../../models/User/User";
import LoadingWheel from "../shared/LoadingWheel";
import { errorResolver } from "../../utils/errorResolver";
import { ChangeUserPasswordModal } from "./ChangeUserPasswordModal";
import { CreateUpdateUserModal } from "./CreateUpdateUserModal";
import { AddExistingUserModal } from "./AddExistingUserModal";
import useCustomerUsers from "../../data/CustomerUser/useCustomerUsers";
import useCustomerUsersMutations from "../../data/CustomerUser/useCustomerUsersMutations";

interface CustomerFeatureListProps {
  customerId: string;
}

const statusClasses = {
  0: "text-gray-700 bg-gray-50 ring-gray-600/20",
  1: "text-red-600 bg-red-50 ring-red-500/10",
  2: "text-green-700 bg-green-50 ring-green-600/20",
  3: "text-blue-700 bg-blue-50 ring-blue-600/20",
};

const statusText = {
  0: "Archived",
  1: "Disabled",
  2: "Enabled",
  3: "Invited",
};

export function CustomerUsersList({ customerId }: CustomerFeatureListProps) {
  const { customerUsers } = useCustomerUsers(customerId);
  const { removeMembership } = useCustomerUsersMutations(customerId);
  const [pageNumber, setPageNumber] = useState(0);
  const [itemsPerPage] = useState(10);
  const [numPages, setNumPages] = useState(0);
  const [users, setUsers] = useState<IUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [addEditUserOpen, setAddEditUserOpen] = useState(false);
  const [addExistingOpen, setAddExistingOpen] = useState(false);
  const [editUser, setEditUser] = useState<IUser>();
  const [passwordUser, setPasswordUser] = useState<IUser>();
  const [rawSearch, setRawSearch] = useState("");
  const searchTerm = useDebounce(rawSearch, 300);

  const setTableData = (
    pageNo: number,
    perPage: number,
    searchTerm?: string
  ) => {
    if (customerUsers.data === null || customerUsers.data === undefined) return;

    setLoading(true);

    let users = customerUsers.data;

    if (searchTerm) {
      users = users.filter(
        (user) =>
          user.forename?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          user.surname?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          user.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          user.email?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    let startIndex = pageNo * perPage;
    let endIndex = startIndex + perPage;

    setUsers(users.slice(startIndex, endIndex));
    setLoading(false);
  };

  const handleNextPage = () => {
    let newPage = pageNumber + 1;

    if (newPage > numPages) return;

    setPageNumber(newPage);
    setTableData(newPage, itemsPerPage);
  };

  const handlePrevPage = () => {
    let newPage = pageNumber - 1;

    if (newPage < 0) return;

    setPageNumber(newPage);
    setTableData(newPage, itemsPerPage);
  };

  const handleCreateUpdateClose = () => {
    setAddEditUserOpen(false);
    editUser &&
      setTimeout(() => {
        setEditUser(undefined);
      }, 250);
  };

  const openChangePassword = (user: IUser) => {
    setAddEditUserOpen(false);
    setTimeout(() => {
      setEditUser(undefined);
    }, 250);
    setPasswordUser(user);
  };

  // Other customers this user belongs to, for the "shared" badge.
  const otherCustomers = (user: IUser) =>
    (user.memberships ?? [])
      .filter((m) => m.customerId !== customerId)
      .map((m) => m.customerName ?? "another customer");

  const confirmRemove = (user: IUser) => {
    const others = otherCustomers(user);
    const name = `${user.forename} ${user.surname}`.trim() || user.username;
    const message =
      others.length > 0
        ? `Remove ${name} from this customer? They will keep their access to ${others.join(", ")}.`
        : `Remove ${name}? This is their only customer, so their login will be deleted.`;

    if (window.confirm(message)) {
      removeMembership.mutate(user.id);
    }
  };

  useEffect(() => {
    setTableData(pageNumber, itemsPerPage, searchTerm);
  }, [searchTerm]);

  useEffect(() => {
    if (
      !customerUsers.isLoading &&
      customerUsers.data &&
      customerUsers.data.length > 0
    ) {
      setNumPages(Math.ceil(customerUsers.data.length / itemsPerPage));

      setTableData(pageNumber, itemsPerPage);
    } else if (!customerUsers.isLoading && customerUsers.data) {
      setUsers([]);
      setLoading(false);
    }
  }, [customerUsers.isLoading, customerUsers.data]);

  return (
    <>
      <CreateUpdateUserModal
        open={addEditUserOpen}
        setOpen={handleCreateUpdateClose}
        customerId={customerId}
        user={editUser}
        onChangePassword={
          editUser ? () => openChangePassword(editUser) : undefined
        }
      />
      <ChangeUserPasswordModal
        open={!!passwordUser}
        setOpen={(value) => {
          const next = typeof value === "function" ? value(!!passwordUser) : value;
          if (!next) setPasswordUser(undefined);
        }}
        customerId={customerId}
        user={passwordUser}
      />
      <AddExistingUserModal
        open={addExistingOpen}
        setOpen={setAddExistingOpen}
        customerId={customerId}
      />
      <Card
        title="Users"
        bodyClassName="p-4"
        titleRightContent={
          <div className="flex items-center gap-2">
            <Button
              onClick={() => setAddExistingOpen(true)}
              size="sm"
              variant="outlined"
              className="my-[1px] flex items-center space-x-2"
            >
              <UserPlusIcon className="h-5 w-5 -ml-1" />
              <p>Add Existing User</p>
            </Button>
            <Button
              onClick={() => setAddEditUserOpen(true)}
              size="sm"
              className="my-[1px] flex items-center space-x-2"
            >
              <PlusIcon className="h-5 w-5 -ml-1" />
              <p>Add User</p>
            </Button>
          </div>
        }
      >
        {!customerUsers.isLoading && (
          <div className="w-full lg:w-1/4 mb-4">
            <TextField
              placeholder="Search..."
              onChange={(e) => setRawSearch(e.currentTarget.value)}
              value={rawSearch}
            />
          </div>
        )}
        {customerUsers.isLoading || loading ? (
          <div className="py-4">
            <LoadingWheel />
          </div>
        ) : customerUsers.isError ? (
          <div className="p-4">
            <Alert
              severity="error"
              title="Error getting customer users"
              message={errorResolver(customerUsers.error)}
            />
          </div>
        ) : (
          <>
            <MiniTable>
              <MiniTable.Header>
                <MiniTable.HeaderCell>Display Name</MiniTable.HeaderCell>
                <MiniTable.HeaderCell>Username</MiniTable.HeaderCell>
                <MiniTable.HeaderCell>Email</MiniTable.HeaderCell>
                <MiniTable.HeaderCell>Status</MiniTable.HeaderCell>
                <MiniTable.HeaderCell>App Ver.</MiniTable.HeaderCell>
                <MiniTable.HeaderCell className="text-center">
                  Actions
                </MiniTable.HeaderCell>
              </MiniTable.Header>
              <MiniTable.Body>
                {users.length > 0 ? (
                  users.map((u, i) => (
                    <MiniTable.Row key={i}>
                      <MiniTable.Cell>
                        <div className="flex items-center gap-2">
                          <span>
                            {u.forename} {u.surname}
                          </span>
                          {otherCustomers(u).length > 0 && (
                            <span
                              title={`Also a member of: ${otherCustomers(u).join(", ")}`}
                              className="whitespace-nowrap rounded-md bg-purple-50 px-1.5 py-0.5 text-xs font-medium text-purple-700 ring-1 ring-inset ring-purple-600/20"
                            >
                              Shared · {otherCustomers(u).length + 1}
                            </span>
                          )}
                        </div>
                      </MiniTable.Cell>
                      <MiniTable.Cell>{u.username}</MiniTable.Cell>
                      <MiniTable.Cell>{u.email}</MiniTable.Cell>
                      <MiniTable.Cell>
                        <p
                          className={clsx(
                            statusClasses[u.status],
                            "w-min mx-auto mt-0.5 whitespace-nowrap rounded-md px-1.5 py-0.5 text-xs font-medium ring-1 ring-inset"
                          )}
                        >
                          {statusText[u.status]}
                        </p>
                      </MiniTable.Cell>
                      <MiniTable.Cell className="text-center">
                        {u.version ? u.version : "-"}
                      </MiniTable.Cell>
                      <MiniTable.Cell className="flex items-center justify-center gap-3">
                        <KeyIcon
                          title="Change password"
                          aria-label="Change password"
                          onClick={() => setPasswordUser(u)}
                          className="h-5 w-5 hover:cursor-pointer"
                        />
                        <PencilIcon
                          title="Edit user"
                          aria-label="Edit user"
                          onClick={() => {
                            setEditUser(u);
                            setAddEditUserOpen(true);
                          }}
                          className="h-5 w-5 hover:cursor-pointer"
                        />
                        <UserMinusIcon
                          title={
                            otherCustomers(u).length > 0
                              ? "Remove from this customer"
                              : "Remove user"
                          }
                          aria-label="Remove from customer"
                          onClick={() => confirmRemove(u)}
                          className="h-5 w-5 hover:cursor-pointer text-red-600"
                        />
                      </MiniTable.Cell>
                    </MiniTable.Row>
                  ))
                ) : (
                  <MiniTable.Row>
                    <MiniTable.Cell colSpan={4} className="py-4 text-center">
                      No Users
                    </MiniTable.Cell>
                  </MiniTable.Row>
                )}
              </MiniTable.Body>
            </MiniTable>
            <MiniTable.Pagination
              totalRows={customerUsers.data?.length ?? 0}
              totalPages={numPages}
              currentPage={pageNumber + 1}
              itemsPerPage={itemsPerPage}
              onPrevClick={handlePrevPage}
              onNextClick={handleNextPage}
            />
          </>
        )}
      </Card>
    </>
  );
}
